import { useEffect, useMemo, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import { api, inr, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  CurrencyInr, Bank, TrendUp, TrendDown, Wallet, ChartPie, Scales, Receipt, Coins, Clock,
  Plus, PencilSimple, Trash, CaretRight, CaretDown, TreeStructure, User, Sparkle,
} from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, BarChart, Bar, Legend } from "recharts";

const TABS = [
  ["overview", "Overview"], ["pnl", "P&L"], ["balance", "Balance sheet"],
  ["cashflow", "Cash flow"], ["gst", "GST"], ["aging", "AR / AP ageing"],
  ["accounts", "Chart of accounts"], ["party", "Party ledger"],
  ["ledger", "Ledger"], ["journal", "Journal"],
];
const PIE_COLORS = ["hsl(var(--tint-terracotta))","hsl(var(--tint-sage))","hsl(var(--tint-plum))","hsl(var(--tint-gold))","hsl(var(--tint-indigo))","hsl(var(--tint-teal))"];

export default function Finance() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [cf, setCf] = useState(null);
  const [gst, setGst] = useState(null);
  const [aging, setAging] = useState(null);
  const [ledger, setLedger] = useState({ entries: [], chart_of_accounts: {} });
  const [journal, setJournal] = useState([]);
  const [accountTree, setAccountTree] = useState({ groups: [] });
  const [enums, setEnums] = useState({ nature_options: [], adjustment_methods: [] });
  const [account, setAccount] = useState("");
  const [showJE, setShowJE] = useState(null);
  const [showAcct, setShowAcct] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyLedger, setPartyLedger] = useState(null);

  const allSubLedgers = useMemo(() => {
    const list = [];
    for (const g of accountTree.groups) for (const c of (g.children || [])) list.push({ ...c, group_name: g.name });
    return list;
  }, [accountTree]);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (tab === "ledger") loadLedger();
    if (tab === "journal") loadJournal();
    if (tab === "accounts" || tab === "party" || tab === "journal") loadAccounts();
  }, [tab, account]);

  const loadAll = async () => {
    try {
      const [s, p, b, c, g, a, e] = await Promise.all([
        api.get("/finance/summary"), api.get("/finance/profit-and-loss"), api.get("/finance/balance-sheet"),
        api.get("/finance/cash-flow"), api.get("/finance/gst-summary"), api.get("/finance/aging"), api.get("/finance/enums"),
      ]);
      setSummary(s.data); setPnl(p.data); setBs(b.data); setCf(c.data); setGst(g.data); setAging(a.data); setEnums(e.data);
      loadAccounts();
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Finance access requires finance_manager role");
      else toast.error("Failed to load finance data");
    }
  };
  const loadAccounts = async () => {
    try { const { data } = await api.get("/finance/accounts/tree"); setAccountTree(data); }
    catch { /* silent */ }
  };
  const loadLedger = async () => {
    try {
      const q = account ? `?account=${account}` : "";
      const { data } = await api.get(`/finance/ledger${q}`); setLedger(data);
    } catch { toast.error("Load failed"); }
  };
  const loadJournal = async () => {
    try { const { data } = await api.get("/finance/journal-entries"); setJournal(data); } catch { toast.error("Load failed"); }
  };
  const loadPartyLedger = async (accountId) => {
    try { const { data } = await api.get(`/finance/accounts/${accountId}/ledger`); setPartyLedger(data); }
    catch { toast.error("Load failed"); }
  };

  const seedAccounts = async () => {
    try { const { data } = await api.post("/finance/accounts/seed"); toast.success(`Seeded ${data.seeded} default accounts`); loadAccounts(); }
    catch { toast.error("Seed failed"); }
  };

  const deleteJE = async (je) => {
    if (!window.confirm(`Delete journal entry ${je.ref}? This cannot be undone.`)) return;
    try { await api.delete(`/finance/journal-entries/${je._id}`); toast.success("Journal entry deleted"); loadJournal(); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="finance-page" className="page-fade">
      <PageHeader
        eyebrow="Module"
        title="Finance & accounting"
        description="Auto-derived General Ledger, Profit & Loss, Balance Sheet, Cash Flow, GST reports, AR/AP ageing plus party-wise sub-ledgers and double-entry journal entries."
        tint="sage" icon={CurrencyInr}
        actions={<>
          {accountTree.groups.length === 0 && (
            <button className="btn-ghost" onClick={seedAccounts} data-testid="finance-seed-accounts"><Sparkle size={14}/> Seed default chart</button>
          )}
          <button className="btn-primary" onClick={()=>setShowJE({ mode: "new" })} data-testid="finance-new-je"><Coins size={14}/> New journal entry</button>
        </>}
      />

      <div className="tab-list mb-6 flex-wrap">
        {TABS.map(([t, l]) => (
          <button key={t} onClick={()=>{ setTab(t); if (t !== "party") { setSelectedParty(null); setPartyLedger(null); } }} data-testid={`finance-tab-${t}`} className={tab===t?"is-active":""}>{l}</button>
        ))}
      </div>

      {tab === "overview" && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 stagger">
            <Kpi label="Revenue (FY)" value={inr(summary.revenue)} icon={TrendUp} tint="sage"/>
            <Kpi label="Net profit" value={inr(summary.net_profit)} icon={ChartPie} tint="forest"/>
            <Kpi label="Bank balance" value={inr(summary.bank_balance)} icon={Bank} tint="indigo"/>
            <Kpi label="Tax payable" value={inr(summary.tax_payable)} icon={Receipt} tint="clay"/>
            <Kpi label="Receivable" value={inr(summary.accounts_receivable)} icon={Wallet} tint="gold"/>
            <Kpi label="Payable" value={inr(summary.accounts_payable)} icon={TrendDown} tint="rose"/>
            <Kpi label="COGS" value={inr(summary.cogs)} icon={Scales} tint="terracotta"/>
            <Kpi label="Gross profit" value={inr(summary.gross_profit)} icon={TrendUp} tint="plum"/>
          </div>
          {cf?.monthly?.length > 0 && (
            <div className="card-elevated mb-6">
              <div className="flex items-center justify-between mb-4">
                <div><div className="label-eyebrow">Cash flow</div><div className="display-3 mt-1">Monthly inflow vs outflow</div></div>
                <div className="text-xs text-muted-foreground tabular">Net · <span className={cf.net >= 0 ? "text-[color:hsl(var(--success))]" : "text-destructive"}>{inr(cf.net)}</span></div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                  <BarChart data={cf.monthly}>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false}/>
                    <XAxis dataKey="month" fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false}/>
                    <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tickFormatter={(v)=>`₹${Math.round(v/1000)}k`}/>
                    <Tooltip formatter={(v)=>inr(v)} contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:10,boxShadow:"var(--shadow-lg)",fontSize:12}}/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    <Bar dataKey="inflow" name="Inflow" fill="hsl(var(--tint-sage))" radius={[6,6,0,0]}/>
                    <Bar dataKey="outflow" name="Outflow" fill="hsl(var(--tint-rose))" radius={[6,6,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "pnl" && pnl && (
        <div className="card-elevated max-w-3xl">
          <div className="label-eyebrow">Profit & loss</div>
          <div className="display-3 mt-1 mb-6">Period · {pnl.period.start.slice(0,10)} → {pnl.period.end.slice(0,10)}</div>
          <div className="space-y-6">
            <FinanceGroup title="Income" rows={pnl.income} total={pnl.total_income} totalLabel="Total income" tone="sage"/>
            <FinanceGroup title="Expenses" rows={pnl.expense} total={pnl.total_expense} totalLabel="Total expenses" tone="rose"/>
            <div className="flex justify-between border-t-2 border-foreground/70 pt-4 font-display text-xl font-semibold">
              <span>Net profit</span><span className={`tabular ${pnl.net_profit >= 0 ? "text-[color:hsl(var(--success))]" : "text-destructive"}`}>{inr(pnl.net_profit)}</span>
            </div>
          </div>
        </div>
      )}

      {tab === "balance" && bs && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="card-elevated"><FinanceGroup title="Assets" rows={bs.assets} total={bs.total_assets} totalLabel="Total assets" tone="sage"/></div>
          <div className="card-elevated">
            <FinanceGroup title="Liabilities" rows={bs.liabilities} total={bs.total_liabilities} totalLabel="Total liabilities" tone="rose"/>
            <div className="mt-6"><FinanceGroup title="Equity" rows={bs.equity} total={bs.total_equity} totalLabel="Total equity" tone="plum"/></div>
          </div>
        </div>
      )}

      {tab === "cashflow" && cf && (
        <div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Kpi label="Total inflow" value={inr(cf.total_inflow)} icon={TrendUp} tint="sage"/>
            <Kpi label="Total outflow" value={inr(cf.total_outflow)} icon={TrendDown} tint="rose"/>
            <Kpi label="Net cash flow" value={inr(cf.net)} icon={Bank} tint="indigo"/>
          </div>
          {cf.monthly?.length > 0 && (
            <div className="card-elevated"><div className="label-eyebrow mb-3">Monthly trend</div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                  <LineChart data={cf.monthly}>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false}/>
                    <XAxis dataKey="month" fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false}/>
                    <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tickFormatter={(v)=>`₹${Math.round(v/1000)}k`}/>
                    <Tooltip formatter={(v)=>inr(v)} contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:10,boxShadow:"var(--shadow-lg)",fontSize:12}}/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    <Line type="monotone" dataKey="net" name="Net" stroke="hsl(var(--foreground))" strokeWidth={2} dot={{r:3}}/>
                    <Line type="monotone" dataKey="inflow" name="Inflow" stroke="hsl(var(--tint-sage))" strokeWidth={1.5} dot={{r:2}}/>
                    <Line type="monotone" dataKey="outflow" name="Outflow" stroke="hsl(var(--tint-rose))" strokeWidth={1.5} dot={{r:2}}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "gst" && gst && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="card-elevated">
            <div className="label-eyebrow">Tax collected</div>
            <div className="mt-4 space-y-3 text-sm">
              <Row label="Taxable value" value={inr(gst.taxable_value)}/>
              <Row label="CGST" value={inr(gst.cgst)}/><Row label="SGST" value={inr(gst.sgst)}/><Row label="IGST" value={inr(gst.igst)}/>
              <div className="flex justify-between font-display text-lg font-semibold pt-3 border-t border-border/70"><span>Total tax</span><span className="tabular">{inr((gst.cgst||0)+(gst.sgst||0)+(gst.igst||0))}</span></div>
              <div className="text-xs text-muted-foreground pt-1">Across {gst.invoice_count} tax invoices this FY.</div>
            </div>
          </div>
        </div>
      )}

      {tab === "aging" && aging && (
        <div className="grid md:grid-cols-2 gap-5">
          {["receivable","payable"].map((kind) => (
            <div key={kind} className="card-elevated">
              <div className="label-eyebrow capitalize">Accounts {kind}</div>
              <div className="display-3 mt-1 mb-5 capitalize">{kind === "receivable" ? "Money owed to us" : "Money we owe"}</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
                  <PieChart>
                    <Pie data={Object.entries(aging[kind].buckets).map(([k,v])=>({name:k,value:v}))} dataKey="value" nameKey="name" outerRadius={80} innerRadius={45} paddingAngle={2}>
                      {Object.keys(aging[kind].buckets).map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                    </Pie>
                    <Tooltip formatter={(v)=>inr(v)} contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:10,boxShadow:"var(--shadow-lg)",fontSize:12}}/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                {Object.entries(aging[kind].buckets).map(([k,v])=>(<div key={k} className="flex justify-between border-b border-border/60 pb-1.5 last:border-b-0"><span className="text-muted-foreground">{k} days</span><span className="tabular">{inr(v)}</span></div>))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "accounts" && (
        <AccountsTab tree={accountTree} onChange={loadAccounts} onOpenAccount={(a)=>{ setSelectedParty(a); loadPartyLedger(a._id); setTab("party"); }}
                    openForm={(preset)=>setShowAcct(preset)}/>
      )}

      {tab === "party" && (
        <PartyLedgerTab tree={accountTree} allSubLedgers={allSubLedgers} selected={selectedParty}
                        partyLedger={partyLedger} onSelect={(a)=>{ setSelectedParty(a); loadPartyLedger(a._id); }}/>
      )}

      {tab === "ledger" && (
        <div>
          <div className="flex gap-3 mb-4 flex-wrap items-center">
            <select value={account} onChange={(e)=>setAccount(e.target.value)} className="input-soft max-w-[260px]" data-testid="ledger-account-filter">
              <option value="">All accounts</option>
              {Object.entries(ledger.chart_of_accounts || {}).map(([k, v]) => <option key={k} value={k}>{k.replace(/_/g," ")} ({v.type})</option>)}
            </select>
            <span className="text-xs text-muted-foreground">{ledger.entries.length} entries</span>
          </div>
          <div className="card-soft p-0 overflow-x-auto">
            <table className="table-modern">
              <thead><tr><th>Date</th><th>Ref</th><th>Account</th><th>Debit</th><th>Credit</th><th>Running</th><th>Party</th><th>Narration</th></tr></thead>
              <tbody>
                {ledger.entries.map((e,i)=>(
                  <tr key={i}>
                    <td className="text-xs text-muted-foreground whitespace-nowrap">{(e.date||"").slice(0,16).replace("T"," ")}</td>
                    <td className="font-mono text-xs">{e.ref}</td>
                    <td className="text-sm capitalize">{(e.account_name || e.account || "").replace(/_/g," ")}</td>
                    <td className="tabular text-[color:hsl(var(--success))]">{e.debit ? inr(e.debit) : "—"}</td>
                    <td className="tabular text-destructive">{e.credit ? inr(e.credit) : "—"}</td>
                    <td className="tabular font-medium">{inr(e.running_balance)}</td>
                    <td className="text-xs text-muted-foreground">{e.party || "—"}</td>
                    <td className="text-xs text-muted-foreground">{e.narration}</td>
                  </tr>
                ))}
                {!ledger.entries.length && <tr><td colSpan={8}><EmptyState icon={Coins} title="No ledger entries" description="Ledger entries appear as sales, purchases and manual JEs are posted."/></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "journal" && (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr><th>Ref</th><th>Date</th><th>Nature</th><th>MoA</th><th>Payment method</th><th>Lines</th><th>Debit</th><th>Credit</th><th>Narration</th><th></th></tr></thead>
            <tbody>
              {journal.map((j)=>{
                const moaSet = Array.from(new Set((j.lines||[]).map(l=>l.method_of_adjustment).filter(Boolean)));
                return (
                <tr key={j._id}>
                  <td className="font-mono text-xs">{j.ref}</td>
                  <td className="text-xs text-muted-foreground">{(j.date||"").slice(0,16).replace("T"," ")}</td>
                  <td className="text-xs capitalize">{(j.nature||"—").replace(/_/g," ")}</td>
                  <td className="text-xs">{moaSet.length ? moaSet.map(m=><span key={m} className="status-chip chip-taupe mr-1 capitalize">{m.replace(/_/g," ")}</span>) : <span className="text-muted-foreground">—</span>}</td>
                  <td className="text-xs capitalize text-muted-foreground">{(j.adjustment_method||"—").replace(/_/g," ")}</td>
                  <td className="tabular">{j.lines?.length || 0}</td>
                  <td className="tabular text-[color:hsl(var(--success))]">{inr(j.total_debit || (j.lines||[]).reduce((a,l)=>a+(+l.debit||(l.dr_or_cr==="dr"?+l.amount:0)||0),0))}</td>
                  <td className="tabular text-destructive">{inr(j.total_credit || (j.lines||[]).reduce((a,l)=>a+(+l.credit||(l.dr_or_cr==="cr"?+l.amount:0)||0),0))}</td>
                  <td className="text-xs text-muted-foreground max-w-xs truncate">{j.narration}</td>
                  <td className="text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={()=>setShowJE({ mode:"edit", je: j })} className="btn-ghost btn-icon" data-testid={`je-edit-${j._id}`} title="Edit"><PencilSimple size={12}/></button>
                      <button onClick={()=>deleteJE(j)} className="btn-ghost btn-icon text-destructive" data-testid={`je-delete-${j._id}`} title="Delete"><Trash size={12}/></button>
                    </div>
                  </td>
                </tr>
              );})}
              {!journal.length && <tr><td colSpan={10}><EmptyState icon={Clock} title="No journal entries" description="Use the 'New journal entry' button to record a balanced adjustment or provisional entry."/></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showJE && (
        <JournalEntryForm mode={showJE.mode} initial={showJE.je} accounts={allSubLedgers} enums={enums} onClose={()=>setShowJE(null)}
                          onSaved={()=>{ setShowJE(null); loadJournal(); loadAll(); }}
                          onCreateAccount={(prefillName)=>setShowAcct({ is_group: false, name: prefillName || "", _fromJE: true })}/>
      )}
      {showAcct && (
        <AccountForm groups={accountTree.groups} initial={showAcct} onClose={()=>setShowAcct(null)} onSaved={()=>{ setShowAcct(null); loadAccounts(); }}/>
      )}
    </div>
  );
}

// -------------------- Sub-components --------------------

function Kpi({ label, value, icon: Icon, tint }) {
  return (
    <div className={`card-elevated tint-${tint || "terracotta"} relative overflow-hidden`} data-testid={`kpi-${label.toLowerCase().replace(/\W+/g,"-")}`}>
      <div className="absolute top-2 right-3 opacity-70" style={{color:"hsl(var(--tint))"}}><Icon size={20} weight="duotone"/></div>
      <div className="label-eyebrow">{label}</div>
      <div className="metric-num mt-2">{value}</div>
    </div>
  );
}
function Row({ label, value }) { return <div className="flex justify-between border-b border-border/60 pb-1.5 last:border-b-0"><span className="text-muted-foreground">{label}</span><span className="tabular font-medium">{value}</span></div>; }
function FinanceGroup({ title, rows, total, totalLabel, tone }) {
  return (
    <div>
      <div className={`label-eyebrow mb-3 tint-${tone}`} style={{color:"hsl(var(--tint))"}}>{title}</div>
      <div className="space-y-2 text-sm">
        {rows.map((r)=>(<div key={r.account} className="flex justify-between"><span className="capitalize text-muted-foreground">{r.account.replace(/_/g," ")}</span><span className="tabular">{inr(r.amount)}</span></div>))}
        <div className="flex justify-between font-display text-lg font-semibold pt-3 border-t border-border/70"><span>{totalLabel}</span><span className="tabular">{inr(total)}</span></div>
      </div>
    </div>
  );
}

// ---- Chart of accounts tab ----
function AccountsTab({ tree, onChange, onOpenAccount, openForm }) {
  const [openMap, setOpenMap] = useState({});
  if (!tree.groups.length) return <div className="card-soft"><EmptyState icon={TreeStructure} title="No accounts yet" description="Click 'Seed default chart' above to create the standard chart of accounts."/></div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><div className="label-eyebrow">Chart of accounts</div><div className="display-3 mt-1">Groups & sub-ledgers</div></div>
        <div className="flex gap-2">
          <button onClick={()=>openForm({ is_group: true })} className="btn-ghost btn-sm" data-testid="acct-new-group"><Plus size={12}/> New group</button>
          <button onClick={()=>openForm({ is_group: false })} className="btn-primary btn-sm" data-testid="acct-new-sub"><Plus size={12}/> New sub-ledger</button>
        </div>
      </div>
      <div className="card-soft p-0">
        {tree.groups.map((g)=>{
          const isOpen = openMap[g._id] !== false;
          return (
            <div key={g._id} className="border-b border-border/60 last:border-b-0">
              <button onClick={()=>setOpenMap({...openMap, [g._id]: !isOpen})} className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-muted/40 transition-colors text-left" data-testid={`acct-group-${g.code}`}>
                {isOpen ? <CaretDown size={14}/> : <CaretRight size={14}/>}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[15px] flex items-center gap-2">
                    {g.name}
                    {g.system && <span className="status-chip chip-plum" data-testid={`acct-group-${g.code}-system`}>System</span>}
                    {!g.active && <span className="status-chip chip-taupe">inactive</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono">{g.code} · {g.account_type}</div>
                </div>
                <span className="text-xs text-muted-foreground tabular">{g.children.length} sub-ledgers</span>
              </button>
              {isOpen && (
                <div className="pl-10 pr-5 pb-3">
                  {g.children.length === 0 && <div className="text-xs text-muted-foreground py-2">No sub-ledgers. Click + New sub-ledger above.</div>}
                  {g.children.map((c)=>(
                    <div key={c._id} className={`flex items-center gap-3 py-2 border-b border-border/40 last:border-b-0 ${!c.active ? "opacity-50" : ""}`} data-testid={`acct-sub-${c.code}`}>
                      <span className="text-sm font-medium flex-1">{c.name}</span>
                      <span className="text-[11px] font-mono text-muted-foreground">{c.code}</span>
                      {c.system && <span className="status-chip chip-plum" data-testid={`acct-sub-${c.code}-system`}>System</span>}
                      {c.party_type && <span className="status-chip chip-taupe">{c.party_type}</span>}
                      {c.gstin && <span className="status-chip chip-taupe font-mono">{c.gstin}</span>}
                      {!c.active && <span className="status-chip chip-taupe">inactive</span>}
                      <button onClick={()=>onOpenAccount(c)} className="btn-ghost btn-sm" data-testid={`acct-open-${c.code}`}>View ledger</button>
                      <button onClick={()=>openForm({ ...c, mode:"edit" })} className="btn-ghost btn-icon" data-testid={`acct-edit-${c.code}`}><PencilSimple size={12}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AccountForm({ groups, initial, onClose, onSaved }) {
  const isEdit = initial?.mode === "edit";
  const isSystem = !!initial?.system;
  const [form, setForm] = useState({
    code: initial?.code || "", name: initial?.name || "",
    parent_id: initial?.parent_id || "", account_type: initial?.account_type || "asset",
    is_group: !!initial?.is_group,
    sub_group: initial?.sub_group || "",
    party_type: initial?.party_type || "",
    opening_balance: initial?.opening_balance || 0,
    opening_dr_cr: initial?.opening_dr_cr || "debit",
    bank_name: initial?.bank_name || "", ac_number: initial?.ac_number || "",
    ifsc: initial?.ifsc || "", branch: initial?.branch || "",
    gstin: initial?.gstin || "", pan: initial?.pan || "",
    notes: initial?.notes || "",
  });
  const currentGroup = groups.find(g => g._id === form.parent_id);
  const effectiveType = form.is_group ? form.account_type : (currentGroup?.account_type || form.account_type);
  const natureLabel = { asset: "Asset", liability: "Liability", equity: "Equity", revenue: "Income", expense: "Expense" }[effectiveType];

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = isEdit
        ? { name: form.name, sub_group: form.sub_group || null,
            party_type: form.party_type || null,
            opening_balance: Number(form.opening_balance || 0), opening_dr_cr: form.opening_dr_cr,
            bank_name: form.bank_name || null, ac_number: form.ac_number || null,
            ifsc: form.ifsc || null, branch: form.branch || null,
            gstin: form.gstin || null, pan: form.pan || null, notes: form.notes || "" }
        : { ...form, opening_balance: Number(form.opening_balance) };
      const { data } = isEdit
        ? await api.patch(`/finance/accounts/${initial._id}`, payload)
        : await api.post("/finance/accounts", payload);
      toast.success(isEdit ? "Account updated" : (data?.opening_je_id ? "Account created · opening JE posted" : "Account created"));
      onSaved && onSaved(data);
    } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Failed"); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="acct-form">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-eyebrow">{isEdit ? "Edit account" : (form.is_group ? "New main group" : "New sub-ledger")}</div>
            <div className="display-3 mt-1">{isEdit ? initial.name : "Create ledger account"}</div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {isSystem && <span className="status-chip chip-plum" data-testid="acct-system-badge">System</span>}
            {natureLabel && <span className="status-chip chip-taupe">{natureLabel}</span>}
          </div>
        </div>

        {isSystem && (
          <div className="mt-4 mb-2 rounded-lg border border-border/60 bg-muted/40 px-4 py-2.5 text-[11.5px] text-muted-foreground" data-testid="acct-system-warning">
            This is a system account. Only Super Admin can rename or deactivate. The accounting nature and code cannot be changed.
          </div>
        )}

        <div className="mt-5 label-eyebrow">Basic details</div>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <label className="block"><span className="label-eyebrow">Code *</span>
            <input required disabled={isEdit} value={form.code} onChange={(e)=>setForm({...form, code:e.target.value.toUpperCase()})} className="input-underline font-mono" data-testid="acct-code"/></label>
          <label className="block"><span className="label-eyebrow">Name *</span>
            <input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="input-underline" data-testid="acct-name"/></label>
          {!form.is_group && !isEdit && (
            <label className="block col-span-2"><span className="label-eyebrow">Under main group *</span>
              <select value={form.parent_id} onChange={(e)=>{const g=groups.find(x=>x._id===e.target.value); setForm({...form, parent_id:e.target.value, account_type: g?.account_type || form.account_type});}} required className="input-soft mt-2" data-testid="acct-parent">
                <option value="">— Select —</option>
                {groups.map((g)=><option key={g._id} value={g._id}>{g.name} ({g.account_type})</option>)}
              </select></label>
          )}
          {(form.is_group || isEdit) && (
            <label className="block"><span className="label-eyebrow">Account type</span>
              <select value={form.account_type} disabled={isEdit && isSystem} onChange={(e)=>setForm({...form, account_type:e.target.value})} className="input-soft mt-2" data-testid="acct-type">
                {["asset","liability","equity","revenue","expense"].map(t=><option key={t} value={t}>{t}</option>)}
              </select></label>
          )}
          {!form.is_group && (
            <label className="block"><span className="label-eyebrow">Sub-group (optional)</span>
              <input value={form.sub_group} onChange={(e)=>setForm({...form, sub_group:e.target.value})} placeholder="e.g. Trade Payables" className="input-underline" data-testid="acct-subgroup"/></label>
          )}
          {!form.is_group && (
            <label className="block"><span className="label-eyebrow">Party type</span>
              <select value={form.party_type||""} onChange={(e)=>setForm({...form, party_type:e.target.value})} className="input-soft mt-2" data-testid="acct-party-type">
                <option value="">—</option>
                {["customer","supplier","bank","employee","other"].map(t=><option key={t} value={t}>{t}</option>)}
              </select></label>
          )}
        </div>

        {!form.is_group && (
          <>
            <div className="mt-6 label-eyebrow">Opening balance <span className="normal-case text-muted-foreground/70">(auto-posts a contra JE to Opening Balance Equity)</span></div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <label className="block"><span className="label-eyebrow">Amount</span>
                <input type="number" step="0.01" value={form.opening_balance} onChange={(e)=>setForm({...form, opening_balance:e.target.value})} className="input-underline tabular" data-testid="acct-ob-amount"/></label>
              <label className="block"><span className="label-eyebrow">DR / CR</span>
                <select value={form.opening_dr_cr} onChange={(e)=>setForm({...form, opening_dr_cr:e.target.value})} className="input-soft mt-2" data-testid="acct-ob-drcr">
                  <option value="debit">Debit</option><option value="credit">Credit</option>
                </select></label>
            </div>

            <div className="mt-6 label-eyebrow">Bank details <span className="normal-case text-muted-foreground/70">(for Bank / party accounts)</span></div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <label className="block"><span className="label-eyebrow">Bank name</span>
                <input value={form.bank_name} onChange={(e)=>setForm({...form, bank_name:e.target.value})} className="input-underline" data-testid="acct-bank-name"/></label>
              <label className="block"><span className="label-eyebrow">Account number</span>
                <input value={form.ac_number} onChange={(e)=>setForm({...form, ac_number:e.target.value})} className="input-underline tabular" data-testid="acct-ac-number"/></label>
              <label className="block"><span className="label-eyebrow">IFSC</span>
                <input value={form.ifsc} onChange={(e)=>setForm({...form, ifsc:e.target.value.toUpperCase()})} className="input-underline font-mono" data-testid="acct-ifsc"/></label>
              <label className="block"><span className="label-eyebrow">Branch</span>
                <input value={form.branch} onChange={(e)=>setForm({...form, branch:e.target.value})} className="input-underline"/></label>
            </div>

            <div className="mt-6 label-eyebrow">Tax details</div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <label className="block"><span className="label-eyebrow">GSTIN</span>
                <input value={form.gstin} onChange={(e)=>setForm({...form, gstin:e.target.value.toUpperCase()})} className="input-underline font-mono" data-testid="acct-gstin"/></label>
              <label className="block"><span className="label-eyebrow">PAN</span>
                <input value={form.pan} onChange={(e)=>setForm({...form, pan:e.target.value.toUpperCase()})} className="input-underline font-mono" data-testid="acct-pan"/></label>
            </div>
          </>
        )}

        <div className="mt-6 label-eyebrow">Notes</div>
        <textarea value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} rows={2} className="input-soft mt-2 w-full" data-testid="acct-notes"/>

        <div className="flex gap-2 mt-8"><button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button><button type="submit" className="btn-primary flex-1" data-testid="acct-submit">{isEdit ? "Save" : "Create account"}</button></div>
      </form>
    </div>,
    document.body,
  );
}

// ---- Searchable Account Picker (for JE lines) with inline "+ Create" ----
function AccountPicker({ accounts, value, onChange, onCreateNew, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = accounts.find(a => a._id === value);
  const filtered = accounts.filter(a => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (a.name || "").toLowerCase().includes(s)
        || (a.code || "").toLowerCase().includes(s)
        || (a.group_name || "").toLowerCase().includes(s);
  }).slice(0, 100);
  return (
    <div className="relative">
      <button type="button" onClick={()=>setOpen(!open)} className="input-cell w-full text-left flex items-center justify-between gap-2" data-testid={testid}>
        {selected ? (
          <span className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-[10.5px] text-muted-foreground shrink-0">{selected.code}</span>
            <span className="truncate">{selected.name}</span>
            {selected.party_type && <span className="status-chip chip-taupe shrink-0">{selected.party_type}</span>}
          </span>
        ) : <span className="text-muted-foreground">— Choose account —</span>}
        <CaretDown size={12}/>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={()=>setOpen(false)}/>
          <div className="absolute z-40 mt-1 w-[min(420px,92vw)] rounded-lg border border-border bg-popover shadow-xl">
            <div className="p-2 border-b border-border/60">
              <input autoFocus placeholder="Search by code, name or group…" value={q} onChange={(e)=>setQ(e.target.value)}
                     className="input-cell w-full" data-testid={`${testid}-search`}/>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No matches</div>}
              {filtered.map((a) => (
                <button type="button" key={a._id} onClick={()=>{ onChange(a._id); setOpen(false); setQ(""); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60 ${a._id===value ? "bg-muted/60" : ""}`}
                        data-testid={`${testid}-opt-${a.code}`}>
                  <span className="font-mono text-[10.5px] text-muted-foreground shrink-0 min-w-[54px]">{a.code}</span>
                  <span className="flex-1 min-w-0 truncate">{a.name}</span>
                  <span className="text-[10.5px] text-muted-foreground shrink-0">{a.group_name || a.account_type}</span>
                  {a.party_type && <span className="status-chip chip-taupe shrink-0">{a.party_type}</span>}
                </button>
              ))}
            </div>
            <button type="button" onClick={()=>{ setOpen(false); onCreateNew && onCreateNew(q); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border-t border-border/60 hover:bg-muted/60 text-foreground"
                    data-testid={`${testid}-create-new`}>
              <Plus size={14}/> Create new account{q ? ` "${q}"` : ""}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---- Party ledger drill-down ----
function PartyLedgerTab({ tree, allSubLedgers, selected, partyLedger, onSelect }) {
  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6">
      <aside>
        <div className="label-eyebrow mb-2">Sub-ledgers</div>
        <input placeholder="Search…" className="input-soft mb-3" onChange={(e)=>{
          const q=e.target.value.toLowerCase();
          document.querySelectorAll("[data-party-item]").forEach(el=>{el.style.display=el.textContent.toLowerCase().includes(q)?"":"none";});
        }} data-testid="party-search"/>
        <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
          {allSubLedgers.length === 0 && <div className="text-xs text-muted-foreground">No sub-ledgers — seed the chart first.</div>}
          {allSubLedgers.map((a)=>(
            <button key={a._id} data-party-item onClick={()=>onSelect(a)} data-testid={`party-item-${a.code}`}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${selected?._id===a._id ? "border-foreground/50 bg-muted/60" : "border-transparent hover:bg-muted/40"}`}>
              <div className="text-sm font-medium">{a.name}</div>
              <div className="text-[11px] text-muted-foreground font-mono">{a.code} · {a.group_name}</div>
            </button>
          ))}
        </div>
      </aside>
      <div>
        {!selected && <div className="card-soft"><EmptyState icon={User} title="Choose a sub-ledger" description="Select any account on the left to see its opening / DR / CR / closing balance and full transaction history."/></div>}
        {selected && partyLedger && (
          <div className="space-y-5">
            <div>
              <div className="label-eyebrow">Sub-ledger</div>
              <div className="display-2 mt-1">{partyLedger.account.name}</div>
              <div className="text-xs text-muted-foreground mt-2 font-mono">{partyLedger.account.code} · {partyLedger.account.account_type}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniKpi label="Opening" value={inr(partyLedger.opening_balance)} sub={partyLedger.opening_dr_cr}/>
              <MiniKpi label="Total debit" value={inr(partyLedger.total_debit)} tone="success"/>
              <MiniKpi label="Total credit" value={inr(partyLedger.total_credit)} tone="destructive"/>
              <MiniKpi label="Closing" value={inr(partyLedger.closing_balance)} sub={partyLedger.closing_dr_cr}/>
            </div>
            <div className="card-soft p-0 overflow-x-auto">
              <table className="table-modern">
                <thead><tr><th>Date</th><th>Ref</th><th>Nature</th><th>Party</th><th>Debit</th><th>Credit</th><th>Running</th><th>Narration</th></tr></thead>
                <tbody>
                  {partyLedger.history.map((h,i)=>(
                    <tr key={i}>
                      <td className="text-xs text-muted-foreground whitespace-nowrap">{(h.date||"").slice(0,16).replace("T"," ")}</td>
                      <td className="font-mono text-xs">{h.ref}</td>
                      <td className="text-xs capitalize">{(h.nature||"—").replace(/_/g," ")}</td>
                      <td className="text-xs">{h.party || "—"}</td>
                      <td className="tabular text-[color:hsl(var(--success))]">{h.debit ? inr(h.debit) : "—"}</td>
                      <td className="tabular text-destructive">{h.credit ? inr(h.credit) : "—"}</td>
                      <td className="tabular font-medium">{inr(h.running_balance)}</td>
                      <td className="text-xs text-muted-foreground">{h.narration}</td>
                    </tr>
                  ))}
                  {!partyLedger.history.length && <tr><td colSpan={8}><EmptyState icon={Clock} title="No transactions" description="No entries posted against this account yet."/></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function MiniKpi({ label, value, sub, tone }) {
  return <div className="card-soft rounded-xl p-4"><div className="label-eyebrow">{label}</div><div className={`metric-num mt-1 ${tone==="success"?"text-[color:hsl(var(--success))]":tone==="destructive"?"text-destructive":""}`} style={{fontSize:22}}>{value}</div>{sub && <div className="text-[10.5px] text-muted-foreground uppercase mt-1 tracking-wider">{sub}</div>}</div>;
}

// ---- Journal entry form (create + edit) ----
function JournalEntryForm({ mode, initial, accounts, enums, onClose, onSaved, onCreateAccount }) {
  const isEdit = mode === "edit";
  const normalize = (line) => {
    if (line.account_id) return {
      account_id: line.account_id, dr_or_cr: line.dr_or_cr || "dr", amount: line.amount ?? 0,
      narration: line.narration || "", party: line.party || "",
      method_of_adjustment: line.method_of_adjustment || "",
      references: line.references || [], new_reference: line.new_reference || {},
    };
    return { account_id: line.account_id || (accounts[0]?._id || ""), dr_or_cr: (+line.debit>0?"dr":"cr"), amount: +line.debit || +line.credit || 0, narration: line.narration || "", party: line.party || "", method_of_adjustment: "", references: [], new_reference: {} };
  };
  const [form, setForm] = useState({
    date: (initial?.date || new Date().toISOString()).slice(0, 10),
    ref: initial?.ref || `JE-${Date.now()}`,
    nature: initial?.nature || "journal",
    adjustment_method: initial?.adjustment_method || "not_applicable",
    narration: initial?.narration || "",
    lines: initial?.lines?.length ? initial.lines.map(normalize) : [{ account_id: "", dr_or_cr: "dr", amount: 0, narration:"", party:"" }, { account_id: "", dr_or_cr: "cr", amount: 0, narration:"", party:"" }],
  });

  const totalDr = form.lines.reduce((a,l)=>a+(l.dr_or_cr==="dr"?+l.amount||0:0), 0);
  const totalCr = form.lines.reduce((a,l)=>a+(l.dr_or_cr==="cr"?+l.amount||0:0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;
  const adjustedAmt = form.lines.reduce((a,l)=>a+((l.method_of_adjustment && l.method_of_adjustment !== "on_account") ? +l.amount||0 : 0), 0);
  const totalAmt = Math.max(totalDr, totalCr);
  const [openRefs, setOpenRefs] = useState({}); // account_id -> [refs]

  const loadRefsFor = async (accountId) => {
    if (!accountId || openRefs[accountId]) return;
    try { const { data } = await api.get(`/finance/accounts/${accountId}/references`); setOpenRefs(prev => ({...prev, [accountId]: data})); }
    catch { /* silent */ }
  };
  const isPartyAcct = (aid) => {
    const a = accounts.find(x => x._id === aid);
    return a && ["customer","supplier","employee"].includes(a.party_type);
  };

  const setLine = (i, patch) => { const ls=[...form.lines]; ls[i]={...ls[i], ...patch}; setForm({...form, lines:ls}); };
  const addLine = () => setForm({...form, lines: [...form.lines, { account_id: "", dr_or_cr: "dr", amount: 0, narration:"", party:"" }]});
  const removeLine = (i) => { if (form.lines.length <= 2) return; setForm({...form, lines: form.lines.filter((_,ii)=>ii!==i)}); };

  const submit = async (e) => {
    e.preventDefault();
    if (!balanced) { toast.error("Total debit must equal total credit"); return; }
    const cleanLines = form.lines.filter(l=>+l.amount > 0 && l.account_id);
    if (cleanLines.length < 2) { toast.error("Need at least 2 valid lines"); return; }
    const payload = { ...form, lines: cleanLines };
    try {
      if (isEdit) await api.patch(`/finance/journal-entries/${initial._id}`, payload);
      else await api.post("/finance/journal-entries", payload);
      toast.success(isEdit ? "Journal entry updated" : "Journal entry posted");
      onSaved && onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="je-form">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label-eyebrow">{isEdit ? "Edit entry" : "New journal entry"}</div>
            <div className="display-3 mt-1">{isEdit ? initial.ref : "Record a balanced double-entry"}</div>
          </div>
          <div className={`status-chip ${balanced ? "chip-emerald" : "chip-rose"}`} data-testid="je-balance-chip">
            {balanced ? "✓ Balanced" : `Off by ${inr(Math.abs(totalDr-totalCr))}`}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <label className="block"><span className="label-eyebrow">Transaction date *</span>
            <input required type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} className="input-underline" data-testid="je-date"/></label>
          <label className="block"><span className="label-eyebrow">Reference</span>
            <input value={form.ref} onChange={(e)=>setForm({...form, ref:e.target.value})} className="input-underline font-mono" data-testid="je-ref"/></label>
          <label className="block"><span className="label-eyebrow">Nature of transaction *</span>
            <select required value={form.nature} onChange={(e)=>setForm({...form, nature:e.target.value})} className="input-soft mt-2" data-testid="je-nature">
              {enums.nature_options.map(n=><option key={n} value={n}>{n.replace(/_/g," ")}</option>)}
            </select></label>
          <label className="block"><span className="label-eyebrow">Adjustment method</span>
            <select value={form.adjustment_method} onChange={(e)=>setForm({...form, adjustment_method:e.target.value})} className="input-soft mt-2" data-testid="je-adjustment">
              {enums.adjustment_methods.map(m=><option key={m} value={m}>{m.replace(/_/g," ")}</option>)}
            </select></label>
          <label className="block col-span-2 md:col-span-4"><span className="label-eyebrow">Narration</span>
            <input value={form.narration} onChange={(e)=>setForm({...form, narration:e.target.value})} className="input-underline" data-testid="je-narration"/></label>
        </div>

        <div className="label-eyebrow mb-3">Lines · debits must equal credits</div>
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern text-sm">
            <thead><tr><th style={{width:"32%"}}>Account</th><th style={{width:"10%"}}>DR/CR</th><th style={{width:"14%"}}>Amount</th><th>Party / narration</th><th style={{width:"48px"}}></th></tr></thead>
            <tbody>
              {form.lines.map((l, i)=>(
                <Fragment key={i}>
                <tr>
                  <td>
                    <AccountPicker accounts={accounts} value={l.account_id}
                      onChange={(aid)=>{ setLine(i, { account_id: aid, method_of_adjustment: "", references: [], new_reference: {} }); loadRefsFor(aid); }}
                      onCreateNew={(prefill)=>{ if (onCreateAccount) onCreateAccount(prefill, i); }}
                      testid={`je-line-${i}-account`}/>
                  </td>
                  <td>
                    <div className="flex rounded-md border border-border overflow-hidden">
                      {["dr","cr"].map(v=>(
                        <button type="button" key={v} onClick={()=>setLine(i, { dr_or_cr: v })} data-testid={`je-line-${i}-${v}`}
                                className={`flex-1 py-1.5 text-xs font-semibold uppercase transition-colors ${l.dr_or_cr===v ? (v==="dr"?"bg-[color:hsl(var(--success)/0.15)] text-[color:hsl(var(--success))]":"bg-destructive/15 text-destructive") : "text-muted-foreground hover:bg-muted/40"}`}>{v}</button>
                      ))}
                    </div>
                  </td>
                  <td><input type="number" step="0.01" min="0" value={l.amount} onChange={(e)=>setLine(i, { amount: +e.target.value })} className="input-cell tabular" data-testid={`je-line-${i}-amount`}/></td>
                  <td>
                    <div className="grid grid-cols-2 gap-1">
                      <input placeholder="Party" value={l.party} onChange={(e)=>setLine(i, { party: e.target.value })} className="input-cell"/>
                      <input placeholder="Narration" value={l.narration} onChange={(e)=>setLine(i, { narration: e.target.value })} className="input-cell"/>
                    </div>
                  </td>
                  <td className="text-right">
                    <button type="button" onClick={()=>removeLine(i)} disabled={form.lines.length<=2} className="btn-ghost btn-icon text-destructive" data-testid={`je-line-${i}-remove`}><Trash size={12}/></button>
                  </td>
                </tr>
                {isPartyAcct(l.account_id) && (
                  <tr>
                    <td colSpan={5} className="!py-2 !px-2 bg-muted/30">
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="text-[10.5px] uppercase tracking-widest text-muted-foreground font-semibold">Method of Adj. *</label>
                        <select required value={l.method_of_adjustment} onChange={(e)=>setLine(i, { method_of_adjustment: e.target.value, references: [], new_reference: {} })} className="input-cell max-w-[180px]" data-testid={`je-line-${i}-moa`}>
                          <option value="">— Select —</option>
                          <option value="advance">Advance</option>
                          <option value="against_reference">Against Reference</option>
                          <option value="new_reference">New Reference</option>
                          <option value="on_account">On Account</option>
                        </select>
                        {l.method_of_adjustment === "new_reference" && (
                          <>
                            <input placeholder="Ref number *" value={l.new_reference?.reference_number || ""} onChange={(e)=>setLine(i, { new_reference: {...l.new_reference, reference_number: e.target.value} })} className="input-cell max-w-[180px]" data-testid={`je-line-${i}-refno`}/>
                            <input type="date" value={l.new_reference?.reference_date || form.date} onChange={(e)=>setLine(i, { new_reference: {...l.new_reference, reference_date: e.target.value} })} className="input-cell max-w-[150px]"/>
                            <input type="date" placeholder="Due date" value={l.new_reference?.due_date || ""} onChange={(e)=>setLine(i, { new_reference: {...l.new_reference, due_date: e.target.value} })} className="input-cell max-w-[150px]"/>
                          </>
                        )}
                        {l.method_of_adjustment === "against_reference" && (
                          <div className="flex-1 min-w-[380px]">
                            {(openRefs[l.account_id] || []).length === 0 ? (
                              <span className="text-xs text-muted-foreground">No open references — pick a different MoA or create a new_reference first.</span>
                            ) : (
                              <div className="space-y-1">
                                {(openRefs[l.account_id] || []).map((r) => {
                                  const existing = (l.references || []).find(x => x.reference_id === r._id);
                                  return (
                                    <div key={r._id} className="flex items-center gap-2 text-xs" data-testid={`je-line-${i}-ref-${r._id}`}>
                                      <input type="checkbox" checked={!!existing} onChange={(e)=>{
                                        const refs = [...(l.references || [])];
                                        if (e.target.checked) refs.push({ reference_id: r._id, reference_number: r.reference_number, adjust_amount: Math.min(+l.amount || 0, r.outstanding_amount) });
                                        else { const idx = refs.findIndex(x=>x.reference_id===r._id); if (idx>=0) refs.splice(idx,1); }
                                        setLine(i, { references: refs });
                                      }} className="w-4 h-4 accent-foreground"/>
                                      <span className="font-mono min-w-[110px]">{r.reference_number}</span>
                                      <span className="text-muted-foreground">out: {inr(r.outstanding_amount)}</span>
                                      <input type="number" step="0.01" placeholder="Adjust amt" disabled={!existing} value={existing?.adjust_amount || ""} onChange={(e)=>{
                                        const refs = (l.references || []).map(x => x.reference_id === r._id ? {...x, adjust_amount: +e.target.value} : x);
                                        setLine(i, { references: refs });
                                      }} className="input-cell tabular max-w-[120px]"/>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-medium">
                <td colSpan={2} className="text-right text-muted-foreground text-xs uppercase tracking-widest pr-3">Totals</td>
                <td className="tabular">
                  <div className="flex items-center gap-2"><span className="text-[10px] uppercase text-[color:hsl(var(--success))]">DR</span><span className="tabular">{inr(totalDr)}</span></div>
                  <div className="flex items-center gap-2 mt-0.5"><span className="text-[10px] uppercase text-destructive">CR</span><span className="tabular">{inr(totalCr)}</span></div>
                </td>
                <td colSpan={2}>
                  <div className="text-[11px] text-muted-foreground flex flex-col gap-0.5">
                    <span>Total: <span className="tabular text-foreground">{inr(totalAmt)}</span></span>
                    <span>Adjusted: <span className="tabular text-[color:hsl(var(--success))]">{inr(adjustedAmt)}</span></span>
                    <span>Unadjusted: <span className="tabular text-destructive">{inr(Math.max(totalAmt - adjustedAmt, 0))}</span></span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <button type="button" onClick={addLine} className="btn-ghost btn-sm mt-3">+ Add line</button>

        <div className="flex gap-2 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={!balanced} className="btn-primary flex-1" data-testid="je-submit">{isEdit ? "Save entry" : "Post entry"}</button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
